"use client"

import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Badge } from "@/components/ui/badge"
import { ChevronDownIcon, SearchIcon } from "lucide-react"
import technologiesData from "../utils/technologies.json"
import { cn } from "@/lib/utils"

// Import the category icons from the original component
import {
  Code2Icon,
  GlobeIcon,
  SmartphoneIcon,
  DatabaseIcon,
  CloudIcon,
  GitBranchIcon,
  NetworkIcon,
  ServerIcon,
  MonitorIcon,
  ShieldIcon,
  BrainIcon,
  BarChartIcon,
  LinkIcon,
  CheckCircleIcon,
  PackageIcon,
  PaletteIcon,
  ActivityIcon,
  LayersIcon,
} from "lucide-react"

// Map category names to their respective icons
const categoryIcons = {
  "Programming Languages": <Code2Icon className="w-4 h-4 mr-2" />,
  "Web Development": <GlobeIcon className="w-4 h-4 mr-2" />,
  "Mobile Development": <SmartphoneIcon className="w-4 h-4 mr-2" />,
  Databases: <DatabaseIcon className="w-4 h-4 mr-2" />,
  "Cloud & DevOps": <CloudIcon className="w-4 h-4 mr-2" />,
  "Version Control": <GitBranchIcon className="w-4 h-4 mr-2" />,
  "APIs & Web Services": <NetworkIcon className="w-4 h-4 mr-2" />,
  "Server Technologies": <ServerIcon className="w-4 h-4 mr-2" />,
  "Operating Systems": <MonitorIcon className="w-4 h-4 mr-2" />,
  Security: <ShieldIcon className="w-4 h-4 mr-2" />,
  "AI & Machine Learning": <BrainIcon className="w-4 h-4 mr-2" />,
  "Data Science": <BarChartIcon className="w-4 h-4 mr-2" />,
  Blockchain: <LinkIcon className="w-4 h-4 mr-2" />,
  "Testing & Automation": <CheckCircleIcon className="w-4 h-4 mr-2" />,
  "Frontend Tools": <PackageIcon className="w-4 h-4 mr-2" />,
  "UI/UX & Design": <PaletteIcon className="w-4 h-4 mr-2" />,
  "Analytics & Monitoring": <ActivityIcon className="w-4 h-4 mr-2" />,
  "Other Technologies": <LayersIcon className="w-4 h-4 mr-2" />,
}

interface TechFilterStandaloneProps {
  value: string | string[]
  onChange: (value: string | string[]) => void
  multiSelect?: boolean
  placeholder?: string
  className?: string
}

export function TechFilterSelect({
  value,
  onChange,
  multiSelect = true,
  placeholder = "Select technologies",
  className,
}: TechFilterStandaloneProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Convert value to array for internal processing
  const selectedValues = useMemo(() => {
    if (multiSelect) {
      return Array.isArray(value) ? value : value ? [value] : []
    } else {
      return value ? [value as string] : []
    }
  }, [value, multiSelect])

  /* const allSkills = useMemo(() => {
    const skills = new Set<string>()
    technologiesData.categories.forEach((category) => {
      category.skills.forEach((skill) => skills.add(skill))
    })
    return Array.from(skills)
  }, []) */

  const filteredCategories = useMemo(() => {
    const query = searchQuery.toLowerCase().trim()

    if (!query) {
      return technologiesData.categories
    }

    return technologiesData.categories
      .map((category) => {
        const filteredSkills = category.skills.filter((skill) => skill.label.toLowerCase().includes(query))

        return {
          ...category,
          skills: filteredSkills,
        }
      })
      .filter((category) => category.skills.length > 0)
  }, [searchQuery])

  const handleSkillChange = useCallback(
    (skill: string) => {
      if (multiSelect) {
        const newValues = selectedValues.includes(skill)
          ? selectedValues.filter((s) => s !== skill)
          : [...selectedValues, skill]
        onChange(newValues)
      } else {
        const newValues = selectedValues.includes(skill)
          ? selectedValues.filter((s) => s !== skill)
          : [skill]
        onChange(newValues)
        setIsOpen(false)
      }
    },
    [selectedValues, onChange, multiSelect],
  )

  /* const handleCategorySelection = useCallback(
    (//category: string, 
      skills: any[]) => {
      if (!multiSelect) return

      const categorySkills = new Set(skills)
      const currentlySelected = new Set(selectedValues)

      const allSelected = skills.every((skill) => currentlySelected.has(skill))

      if (allSelected) {
        const newValues = selectedValues.filter((skill) => !categorySkills.has(skill))
        onChange(newValues)
      } else {
        const newSkills = skills.filter((skill) => !currentlySelected.has(skill))
        onChange([...selectedValues, ...newSkills])
      }
    },
    [selectedValues, onChange, multiSelect],
  ) */

  const handleClearSelection = useCallback(() => {
    onChange(multiSelect ? [] : "")
  }, [onChange, multiSelect])

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  const displayValue = useMemo(() => {
    if (selectedValues.length === 0) {
      return placeholder
    } else if (selectedValues.length === 1) {
      return selectedValues[0]
    } else {
      return `${selectedValues.length} technologies selected`
    }
  }, [selectedValues, placeholder])

  return (
    <div className={cn("relative", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            className="w-full justify-between h-10 px-3 font-normal"
          >
            <span className="truncate">{displayValue}</span>
            <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[calc(100vw-2rem)] sm:w-96 p-4 bg-white" align="start">
          <div className="space-y-4">
            <div className="relative mb-2">
              <SearchIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Search technologies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-8 w-8 p-0"
                  onClick={() => setSearchQuery("")}
                >
                  ×
                </Button>
              )}
            </div>

            <ScrollArea className="h-[300px] pr-4">
              {multiSelect ? (
                <Accordion type="multiple" className="w-full">
                  {filteredCategories.map((category) => (
                    <AccordionItem key={category.name} value={category.name}>
                      <AccordionTrigger className="py-2">
                        <div className="flex items-center justify-between w-full pr-2">
                          <div className="flex items-center">
                            {categoryIcons[category.name as keyof typeof categoryIcons]}
                            <span>{category.name}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {category.skills.filter((skill) => selectedValues.includes(skill.label)).length} /{" "}
                            {category.skills.length}
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="ml-6 space-y-1">
                          {/* <div className="flex items-center space-x-2 mb-1">
                            <Checkbox
                              id={`select-all-${category.name}`}
                              checked={
                                category.skills.length > 0 &&
                                category.skills.every((skill) => selectedValues.includes(skill.label))
                              }
                              onCheckedChange={() => handleCategorySelection(category.skills.map((skill)=>skill.label))}
                              className="h-4 w-4"
                            />
                            <Label
                              htmlFor={`select-all-${category.name}`}
                              className="text-sm font-medium cursor-pointer"
                            >
                              Select All
                            </Label>
                          </div> */}
                          {category.skills.map((skill) => (
                            <div key={skill.label} className="flex items-center space-x-2">
                              <Checkbox
                                id={`${category.name}-${skill}`}
                                checked={selectedValues.includes(skill.label)}
                                onCheckedChange={() => handleSkillChange(skill.label)}
                                className="h-4 w-4"
                                disabled={selectedValues.length>=5}
                              />
                              <Label
                                htmlFor={`${category.name}-${skill}`}
                                className="text-sm font-normal cursor-pointer"
                              >
                                {skill.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <RadioGroup value={selectedValues[0] || ""} onValueChange={handleSkillChange}>
                  <Accordion type="multiple" className="w-full">
                    {filteredCategories.map((category) => (
                      <AccordionItem key={category.name} value={category.name}>
                        <AccordionTrigger className="py-2">
                          <div className="flex items-center justify-between w-full pr-2">
                            <div className="flex items-center">
                              {categoryIcons[category.name as keyof typeof categoryIcons]}
                              <span>{category.name}</span>
                            </div>
                            {selectedValues.some((val) => category.skills.map((skill)=>skill.label).includes(val)) && (
                              <div className="text-xs text-primary">Selected</div>
                            )}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="ml-6 space-y-1">
                            {category.skills.map((skill) => (
                              <div key={skill.label} className="flex items-center space-x-2">
                                <RadioGroupItem id={`${category.name}-${skill}`} value={skill.label} className="h-4 w-4" />
                                <Label
                                  htmlFor={`${category.name}-${skill}`}
                                  className="text-sm font-normal cursor-pointer"
                                >
                                  {skill.label}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </RadioGroup>
              )}

              {filteredCategories.length === 0 && (
                <div className="py-4 text-center text-muted-foreground">
                  No technologies found matching "{searchQuery}"
                </div>
              )}
            </ScrollArea>
          </div>

          <div className="flex justify-between pt-4 border-t mt-4">
            <Button onClick={handleClearSelection} variant="outline" size="sm" disabled={selectedValues.length === 0}>
              Clear
            </Button>
            {multiSelect && (
              <Button onClick={() => setIsOpen(false)} size="sm">
                Done {selectedValues.length > 0 && `(${selectedValues.length})`}
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {multiSelect && selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedValues.map((skill) => (
            <Badge key={skill} variant="secondary" className="flex items-center gap-1">
              {skill}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={(e) => {
                  e.preventDefault()
                  handleSkillChange(skill)
                }}
              >
                <span className="sr-only">Remove {skill}</span>×
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

